import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
  Grid,
  Chip,
  IconButton,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  Close as CloseIcon,
  Dashboard as GridIcon,
  Shuffle as ShuffleIcon,
  EmojiEvents as TrophyIcon,
  Payments as PayoutIcon,
} from '@mui/icons-material';
import type { Board } from '../../types';

interface HowToPlayModalProps {
  open: boolean;
  onClose: () => void;
  board?: Board;
}

export const HowToPlayModal: React.FC<HowToPlayModalProps> = ({ open, onClose, board }) => {
  const pricePerSquare = board?.pricePerSquare ?? 10;
  const homeTeam = board?.homeTeam ?? 'Home Team';
  const awayTeam = board?.awayTeam ?? 'Away Team';
  const quarters = board?.quarters ?? 4;

  const quarterLabels = quarters === 2
    ? ['1st Half', '2nd Half']
    : quarters === 3
      ? ['Period 1', 'Period 2', 'Period 3']
      : ['Q1', 'Q2', 'Q3', 'Q4'];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrophyIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">How to Play Squares</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Introduction */}
        <Typography variant="body1" paragraph>
          A squares pool is a fun prediction game tied to sports scores. Each square on the 10x10 grid
          represents a possible combination of the last digit of each team's score.
        </Typography>

        {/* Step-by-step Guide */}
        <Grid container spacing={3}>
          {/* Step 1: The Grid */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: 14,
                  }}
                >
                  1
                </Box>
                <GridIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">Claim Your Squares</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Click on any available (empty) square to claim it. Each square costs <strong>${pricePerSquare.toFixed(2)}</strong>.
                You can claim multiple squares to increase your chances of winning.
              </Typography>
            </Paper>
          </Grid>

          {/* Step 2: Random Numbers */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: 14,
                  }}
                >
                  2
                </Box>
                <ShuffleIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">Numbers Are Randomized</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Once all squares are claimed, the numbers 0-9 are randomly assigned to each row
                ({awayTeam}) and column ({homeTeam}). This ensures fair play.
              </Typography>
            </Paper>
          </Grid>

          {/* Step 3: How Winners Work */}
          <Grid size={12}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'success.main', bgcolor: 'success.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: 14,
                  }}
                >
                  3
                </Box>
                <TrophyIcon color="success" />
                <Typography variant="subtitle1" fontWeight="bold" color="success.dark">Finding the Winner</Typography>
              </Box>
              <Typography variant="body2" paragraph>
                At the end of each quarter/period, the winning square is determined by the last digit of each team's score:
              </Typography>
              <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="body2" color="text.secondary">Example Score:</Typography>
                    <Typography variant="h6">
                      {homeTeam} <strong>17</strong>, {awayTeam} <strong>24</strong>
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="body2" color="text.secondary">Last Digits:</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip label={`${homeTeam}: 7`} size="small" color="primary" />
                      <Chip label={`${awayTeam}: 4`} size="small" color="error" />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="body2" color="text.secondary">Winner:</Typography>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      The square where row 4 meets column 7
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              <Typography variant="body2" color="text.secondary">
                The same square can win multiple times if the score's last digits match at different quarters!
              </Typography>
            </Paper>
          </Grid>

          {/* Step 4: Payouts */}
          <Grid size={12}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: 14,
                  }}
                >
                  4
                </Box>
                <PayoutIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">Payouts Per Quarter</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Winners are determined at the end of each quarter/period. The prize pool is split among quarters:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {quarterLabels.map((label, index) => (
                  <Chip
                    key={label}
                    label={label}
                    variant={index === quarterLabels.length - 1 ? 'filled' : 'outlined'}
                    color={index === quarterLabels.length - 1 ? 'success' : 'default'}
                    size="small"
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                The final quarter/period typically has the largest payout!
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Quick Tips */}
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Quick Tips
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 0 }}>
          <Typography component="li" variant="body2" color="text.secondary">
            In football, digits 0, 3, and 7 are more common due to scoring patterns
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            All squares have equal chances before numbers are randomized
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            Watch for the highlighted square after each quarter to see the winner
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Link component={RouterLink} to="/support/game-mechanics" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          Learn more about game mechanics
        </Link>
        <Button variant="contained" onClick={onClose}>
          Got It!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HowToPlayModal;
